/**
 * Login.
 *
 * Tras entrar: un ADMIN sin centros todavía va al wizard de primera
 * configuración; todo el resto, a fichar.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { WIZARD_DONE_KEY } from './onboarding/WelcomeWizard';
import { syncService } from '../services/syncService';
import { ThemeToggle } from './ui/ThemeToggle';
import { Icon } from './ui/Icon';
import { PasswordField } from './ui/PasswordField';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useAppStore();
  const navigate = useNavigate();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const api = await import('../services/apiClient');
      await api.login({ email, password });
      const user = await api.getCurrentUser();

      setUser({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        role: user.role as 'ADMIN' | 'MANAGER' | 'EMPLOYEE',
        tenantId: user.tenant_id,
      });

      syncService.start(user.id);

      const isAdmin = user.role === 'ADMIN' || user.role === 'MANAGER';

      // Primera vez de un admin sin centros: al wizard, no a un panel vacío.
      if (user.role === 'ADMIN' && !localStorage.getItem(WIZARD_DONE_KEY)) {
        try {
          const sites = await api.getWorksites();
          if (sites.length === 0) {
            navigate('/bienvenida');
            return;
          }
          localStorage.setItem(WIZARD_DONE_KEY, '1');
        } catch {
          /* sin permiso o sin red: sigue abajo */
        }
      }

      // Un admin gestiona; aterriza en el panel, no en fichar (puede fichar
      // desde el propio panel). El trabajador va directo a fichar.
      navigate(isAdmin ? '/admin/workers' : '/');
    } catch (err: unknown) {
      const apiError = (err as { apiError?: { message: string } })?.apiError;
      setError(apiError?.message ?? 'Correo o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stage flex flex-col items-center safe-area-top safe-area-bottom">
      {/* barra superior */}
      <div className="w-full max-w-[440px] flex items-center justify-between px-6 pt-6 pb-2">
        <div className="flex items-center gap-2.5">
          <span className="w-3 h-3 rounded-full bg-brand" />
          <span className="font-display font-black text-[22px] tracking-tight">kakuat</span>
        </div>
        <ThemeToggle />
      </div>

      {/* tarjeta centrada — en escritorio no se estira a pantalla completa */}
      <div className="w-full flex-1 flex justify-center px-4 pb-8 pt-4">
        <div
          className="w-full max-w-[440px] self-start bg-canvas border border-line-strong rounded-3xl p-7 sm:p-9 animate-rise"
          style={{ boxShadow: '0 40px 90px -55px rgba(0,0,0,.5)' }}
        >
          <h1 className="font-display font-extrabold text-[28px] tracking-tight mb-2">Inicia sesión</h1>
          <p className="text-ink-2 text-[15.5px] mb-7">Registro de jornada de tu empresa.</p>

          <form onSubmit={submit} className="flex flex-col gap-4">
            {error && (
              <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14.5px]">{error}</p>
            )}

            <label className="flex flex-col gap-1.5">
              <span className="label !mb-0">Correo</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="nombre@empresa.es"
                className="field bg-surface"
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="label !mb-0">Contraseña</span>
              <PasswordField
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </label>

            <Link to="/recuperar" className="self-start text-brand font-semibold text-[14.5px] no-underline">
              He olvidado mi contraseña
            </Link>

            <button type="submit" disabled={loading} className="btn-primary mt-1.5">
              {loading ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <div className="text-center mt-6 text-[14.5px] text-ink-2">
            ¿Aún no usáis Kakuat?{' '}
            <Link to="/registro" className="text-brand font-semibold no-underline">
              Apuntaos a la lista de espera
            </Link>
          </div>

          <div className="text-center mt-2 text-[14.5px] text-ink-2">
            ¿No tienes correo?{' '}
            <Link to="/recuperar-acceso" className="text-brand font-semibold no-underline">
              Recupera el acceso con el código del centro
            </Link>
          </div>

          <a
            href="/kiosk"
            className="mt-5 h-12 rounded-xl border border-dashed border-line-strong text-ink-2 font-semibold text-[15px] flex items-center justify-center gap-2"
          >
            <Icon name="kiosk" size={18} strokeWidth={1.8} />
            Usar kiosco compartido
          </a>
        </div>
      </div>
    </div>
  );
}
